function deriveRestUrl(): string {
  if (import.meta.env.VITE_REST_URL) return import.meta.env.VITE_REST_URL as string;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host.endsWith('.local.dabblewriter.com') || host === 'localhost') return 'https://rest.local.dabblewriter.com/';
    if (host.endsWith('.test.dabblewriter.com')) return 'https://rest.test.dabblewriter.com/';
  }
  if (import.meta.env.VITE_DEBUG) return 'https://rest.local.dabblewriter.com/';
  return 'https://rest.dabblewriter.com/';
}

const restUrl = deriveRestUrl();

const TEST_STRIPE_PUBLISHABLE_KEY = 'pk_test_MTjhC6rkHFM6egqJz3xp714J';
const LIVE_STRIPE_PUBLISHABLE_KEY = 'pk_live_jz2vNh33pvMngM7J3gO0Ej6U';

/**
 * Publishable key for the same Stripe account as dabble-rest `STRIPE_KEY`.
 *
 * Defaults are host-derived:
 *   - localhost / *.local.dabblewriter.com → test key (local dabble-rest typically uses sk_test_…)
 *   - *.test.dabblewriter.com              → live key (test REST currently wired to live secrets)
 *   - production hostname                  → live key
 *
 * Override with `VITE_STRIPE_PUBLISHABLE_KEY` when local dabble-rest is wired to
 * the live secret and Elements must use `pk_live_…` to match real customer/product IDs.
 *
 * 2026-07-05: the test environment moved to the Stripe sandbox (test rest now
 * holds an sk_test secret — dabble-rest#213), so `.test.dabblewriter.com` uses
 * the test publishable key to match.
 */
function deriveStripeKey(): string {
  const explicit =
    (typeof import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY === 'string' &&
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY.trim()) ||
    (typeof import.meta.env.VITE_STRIPE_KEY === 'string' && import.meta.env.VITE_STRIPE_KEY.trim());
  if (explicit) return explicit;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host.endsWith('.local.dabblewriter.com') || host.endsWith('.test.dabblewriter.com')) {
      return TEST_STRIPE_PUBLISHABLE_KEY;
    }
  }
  return LIVE_STRIPE_PUBLISHABLE_KEY;
}

const DEFAULT_SENTRY_DSN = 'https://f43b76ff950c6601784bdc378ac8d843@o225830.ingest.us.sentry.io/4511418125385728';
const DEFAULT_POSTHOG_TOKEN = 'phc_xR6X3PTdjheARRZ67RzmhYZ2oFBxs29TinsBZsHDimzJ';
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';
// Google Identity Services OAuth client id — the Firebase Google sign-in web
// client in the dabble-writer Google Cloud project (Firebase console →
// Authentication → Google → Web SDK configuration), which carries the
// authorized accounts.* JavaScript origins. Not the Drive-export client, which
// shares the project prefix but rejects these origins. OAuth client ids are
// public by design — same baked-default convention as Sentry DSN / PostHog.
const DEFAULT_GOOGLE_CLIENT_ID = '890907808375-ee4eknnuuprq16hd4520e73cp0ucud7a.apps.googleusercontent.com';

function deriveEnvironment(): 'local' | 'test' | 'production' {
  if (typeof window === 'undefined') return 'production';
  const host = window.location.hostname;
  if (host === 'localhost' || host.endsWith('.local.dabblewriter.com')) return 'local';
  if (host.endsWith('.test.dabblewriter.com')) return 'test';
  return 'production';
}

const ENVIRONMENT = deriveEnvironment();

const explicitSentryDsn = typeof import.meta.env.VITE_SENTRY_DSN === 'string' && import.meta.env.VITE_SENTRY_DSN.trim();
const explicitPosthogKey =
  typeof import.meta.env.VITE_POSTHOG_KEY === 'string' && import.meta.env.VITE_POSTHOG_KEY.trim();
const explicitPosthogHost =
  typeof import.meta.env.VITE_POSTHOG_HOST === 'string' && import.meta.env.VITE_POSTHOG_HOST.trim();
const explicitGoogleClientId =
  typeof import.meta.env.VITE_GOOGLE_CLIENT_ID === 'string' && import.meta.env.VITE_GOOGLE_CLIENT_ID.trim();

export const REST_URL = restUrl;
export const DOMAIN = 'dabblewriter.com';
export const STRIPE_PUBLISHED_KEY = deriveStripeKey();
export const SENTRY_DSN = explicitSentryDsn || DEFAULT_SENTRY_DSN;
export const SENTRY_ENVIRONMENT = ENVIRONMENT;
// Release identifier (the app version), injected at build time by the deploy
// workflow as VITE_SENTRY_RELEASE. Tagging events with the same value the deploy
// step registers as a Sentry release is what lets "Fixes <ID>" commits auto-resolve
// issues for the shipped release — and reopen only on a genuine regression in a
// later release. Undefined in local/dev builds, so Sentry simply omits the tag.
const explicitSentryRelease =
  typeof import.meta.env.VITE_SENTRY_RELEASE === 'string' && import.meta.env.VITE_SENTRY_RELEASE.trim();
export const SENTRY_RELEASE = explicitSentryRelease || undefined;
export const POSTHOG_TOKEN = explicitPosthogKey || DEFAULT_POSTHOG_TOKEN;
export const POSTHOG_HOST = explicitPosthogHost || DEFAULT_POSTHOG_HOST;
export const POSTHOG_ENVIRONMENT = ENVIRONMENT;
// Override with `VITE_GOOGLE_CLIENT_ID` to point a build at a different OAuth client.
export const GOOGLE_CLIENT_ID = explicitGoogleClientId || DEFAULT_GOOGLE_CLIENT_ID;

// Ad / conversion tracking ids (Google Analytics + Ads, Facebook Pixel,
// Pinterest). Unlike Sentry/PostHog these have NO baked defaults: they're
// VITE-only so they're absent from local/test/fork builds, and are injected in
// production as GitHub Actions Variables by the deploy workflow. Combined with
// the production-host gate below, tracking only fires on the live app. Static
// `import.meta.env.VITE_*` access is required for Vite's build-time inlining.
function trimmedEnv(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
export const GA_MEASUREMENT_ID = trimmedEnv(import.meta.env.VITE_GA_MEASUREMENT_ID);
export const GOOGLE_ADS_ID = trimmedEnv(import.meta.env.VITE_GOOGLE_ADS_ID);
export const FB_PIXEL_ID = trimmedEnv(import.meta.env.VITE_FB_PIXEL_ID);
export const PINTEREST_TAG_ID = trimmedEnv(import.meta.env.VITE_PINTEREST_TAG_ID);
// Ad/conversion tracking runs ONLY on the exact production host. `ENVIRONMENT`
// ('production') is too broad: `deriveEnvironment()` maps every non-local/-test
// host — including `*.beta.dabblewriter.com` and `*.workers.dev` preview URLs,
// which `deploy:beta` builds with the prod backend — to 'production'. Gating on
// the literal host keeps real conversions off beta and previews.
export const MARKETING_ENABLED = typeof window !== 'undefined' && window.location.hostname === `accounts.${DOMAIN}`;

// Tolt affiliate program public key. Baked in rather than injected as a deploy
// variable: it's a `pk_` publishable id (public by design, like the Stripe key
// above) and affiliate attribution must not silently switch off because a
// variable went missing. Off the production host it stays undefined, keeping Tolt
// inert, unless a build explicitly sets `VITE_TOLT_ID`. That's how you exercise the
// integration against a test Tolt program.
const DEFAULT_TOLT_ID = 'pk_P4EPw5pQDZ58LeBpfH5gMkFN';
export const TOLT_ID = trimmedEnv(import.meta.env.VITE_TOLT_ID) || (MARKETING_ENABLED ? DEFAULT_TOLT_ID : undefined);

// ProfitWell Retain public token, baked in for the same reason as Tolt. Retain's
// failed-payment emails link to /billing/portal with a `__pw_iic` token that lets
// a customer update their card without signing in, and only this script reads it.
// The deploy variable was never set on this repo, so from July to November 2026
// every production build shipped without it. The token is the one the writer app
// already serves in its own profitwell.js script URL.
const DEFAULT_PROFITWELL_TOKEN = 'bb17b8197df83fb9a2c12330190c1dea';
export const PROFITWELL_TOKEN =
  trimmedEnv(import.meta.env.VITE_PROFITWELL_TOKEN) || (MARKETING_ENABLED ? DEFAULT_PROFITWELL_TOKEN : undefined);

// Rewardful program id, gated the same way. Dabble is migrating to Tolt; both run
// until the affiliates have moved over, so this id is expected to be deleted.
const DEFAULT_REWARDFUL_ID = '7e3b86';
export const REWARDFUL_ID =
  trimmedEnv(import.meta.env.VITE_REWARDFUL_ID) || (MARKETING_ENABLED ? DEFAULT_REWARDFUL_ID : undefined);
